/**
 * TASK-VIS-004: Combat Log UI Component
 *
 * Elegant scrolling log showing combat events in real-time.
 * Positioned at bottom-right of arena.
 */

import React, { useEffect, useRef, useState } from 'react';
import { CombatLogEntry, getCombatLogService } from '../../services/combat-log-service';

interface CombatLogProps {
  maxHeight?: number;
  width?: number;
}

// Color map for entry types
const TYPE_COLORS: Record<string, string> = {
  ability: '#64B5F6',   // Blue
  damage: '#EF5350',    // Red
  death: '#9E9E9E',     // Gray
  movement: '#81C784',  // Green
  buff: '#FFD54F',      // Yellow
  system: '#B39DDB',    // Purple
};

export const CombatLog: React.FC<CombatLogProps> = ({
  maxHeight = 180,
  width = 280
}) => {
  const [entries, setEntries] = useState<CombatLogEntry[]>([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Subscribe to combat log service
  useEffect(() => {
    const service = getCombatLogService();
    const unsubscribe = service.subscribe((newEntries) => {
      setEntries(newEntries);
    });
    return unsubscribe;
  }, []);

  // Auto-scroll to bottom when new entries arrive (unless user is hovering)
  useEffect(() => {
    if (scrollRef.current && !isHovered) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries, isHovered]);

  if (isMinimized) {
    return (
      <div
        onClick={() => setIsMinimized(false)}
        style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: 8,
          padding: '8px 12px',
          cursor: 'pointer',
          color: '#fff',
          fontSize: 12,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          zIndex: 100,
        }}
      >
        Combat Log ({entries.length})
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        width,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        borderRadius: 8,
        overflow: 'hidden',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        zIndex: 100,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}
      >
        <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
          Combat Log
        </span>
        <button
          onClick={() => setIsMinimized(true)}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.6)',
            cursor: 'pointer',
            fontSize: 16,
            padding: '0 4px',
            lineHeight: 1,
          }}
          title="Minimize"
        >
          -
        </button>
      </div>

      {/* Scrollable content */}
      <div
        ref={scrollRef}
        style={{
          maxHeight,
          overflowY: 'auto',
          padding: '4px 0',
          // Custom scrollbar
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.3) transparent',
        }}
      >
        {entries.length === 0 ? (
          <div style={{
            padding: '16px 12px',
            color: 'rgba(255, 255, 255, 0.4)',
            fontSize: 12,
            textAlign: 'center',
            fontStyle: 'italic',
          }}>
            Waiting for combat...
          </div>
        ) : (
          entries.map((entry, index) => (
            <CombatLogEntryRow
              key={entry.id}
              entry={entry}
              isNew={index === entries.length - 1}
            />
          ))
        )}
      </div>

      {/* Fade overlay at top */}
      <div
        style={{
          position: 'absolute',
          top: 36, // Below header
          left: 0,
          right: 0,
          height: 20,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
};

// Individual log entry row
interface CombatLogEntryRowProps {
  entry: CombatLogEntry;
  isNew: boolean;
}

const CombatLogEntryRow: React.FC<CombatLogEntryRowProps> = ({ entry, isNew }) => {
  const [opacity, setOpacity] = useState(isNew ? 0 : 1);

  // Fade in animation for new entries
  useEffect(() => {
    if (isNew) {
      const timer = setTimeout(() => setOpacity(1), 50);
      return () => clearTimeout(timer);
    }
  }, [isNew]);

  return (
    <div
      style={{
        padding: '4px 12px',
        fontSize: 12,
        lineHeight: 1.4,
        color: TYPE_COLORS[entry.type] || '#fff',
        opacity,
        transition: 'opacity 0.2s ease-in',
        borderLeft: `2px solid ${TYPE_COLORS[entry.type] || '#fff'}`,
        marginLeft: 4,
        backgroundColor: isNew ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
      }}
    >
      <span style={{ marginRight: 6 }}>{entry.icon}</span>
      <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{entry.message}</span>
    </div>
  );
};

export default CombatLog;
